// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

/**
 * useBiometricAuth - React hook for biometric authentication (AA5)
 *
 * Provides biometric availability, type detection, enable/disable,
 * and biometric login attempt functionality.
 */

import { useState, useEffect, useCallback } from 'react';
import { Preferences } from '@capacitor/preferences';
import {
  isBiometricAvailable,
  getBiometricType,
  authenticateWithBiometric,
  storeBiometricToken,
  getBiometricToken,
  clearBiometricToken,
  ENABLED_KEY,
  type BiometricType,
} from '../utils/biometrics';

interface UseBiometricAuthReturn {
  biometricAvailable: boolean;
  biometricType: BiometricType;
  biometricEnabled: boolean;
  enableBiometric: (deviceToken: string, expiresIn: number) => Promise<void>;
  disableBiometric: () => Promise<void>;
  attemptBiometricLogin: () => Promise<string | null>;
}

export function useBiometricAuth(): UseBiometricAuthReturn {
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricType, setBiometricType] = useState<BiometricType>('none');
  const [biometricEnabled, setBiometricEnabled] = useState(false);

  // Initialize on mount
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      const [available, type] = await Promise.all([
        isBiometricAvailable(),
        getBiometricType(),
      ]);

      if (cancelled) return;
      setBiometricAvailable(available);
      setBiometricType(type);

      // Check if user has opted in
      try {
        const { value } = await Preferences.get({ key: ENABLED_KEY });
        if (!cancelled) {
          setBiometricEnabled(value === 'true');
        }
      } catch {
        // Preferences not available (web)
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, []);

  const enableBiometric = useCallback(async (deviceToken: string, expiresIn: number) => {
    await storeBiometricToken(deviceToken, expiresIn);
    try {
      await Preferences.set({ key: ENABLED_KEY, value: 'true' });
    } catch {
      // fallback
    }
    setBiometricEnabled(true);
  }, []);

  const disableBiometric = useCallback(async () => {
    await clearBiometricToken();
    setBiometricEnabled(false);
  }, []);

  const attemptBiometricLogin = useCallback(async (): Promise<string | null> => {
    // Trigger native biometric prompt
    const authenticated = await authenticateWithBiometric();
    if (!authenticated) return null;

    // Retrieve stored device token
    const token = await getBiometricToken();
    return token;
  }, []);

  return {
    biometricAvailable,
    biometricType,
    biometricEnabled,
    enableBiometric,
    disableBiometric,
    attemptBiometricLogin,
  };
}
